/**
 * 변경사항 네비게이터
 *
 * DSD-DSD 대사 결과에서 변경된 셀/문단/span을 순서대로 수집하고,
 * 플로팅 패널로 이전/다음 탐색을 지원.
 */

import type {
  DiffCell,
  DiffDsdTable,
  DiffParagraph,
  DiffParagraphGroup,
  DiffSpan,
  DsdDsdCompareItem,
} from "@/apis/dsd-dsd-compare/types";
import { TextChip } from "@/toggle-ui-kit/chip";
import {
  ChevronLeft,
  ChevronRight,
  CircleDashed,
  CirclePlus,
  CircleX,
  TriangleAlert,
} from "lucide-react";
import { useEffect } from "react";
import {
  isChangesActive,
  isSumDiffsActive,
  type DsdDsdViewMode,
} from "./compare-header";

// ============================================
// ChangeEntry 타입
// ============================================

export interface CellChangeEntry {
  type: "cell";
  cellIds: string[];
  srcCell: DiffCell | null;
  trgCell: DiffCell | null;
  sectionName: string;
}

export interface ParagraphChangeEntry {
  type: "paragraph";
  paragraphId: string;
  srcParagraph: DiffParagraph | null;
  trgParagraph: DiffParagraph | null;
  sectionName: string;
}

/** modified 문단 내 연속된 변경 span 그룹 */
export interface SpanChangeEntry {
  type: "span";
  spanChangeId: string;
  /** 변경 span 범위 [start, end) */
  spanRange: [number, number];
  /** 삭제된 텍스트 (delete spans) */
  deletedText: string;
  /** 추가된 텍스트 (insert spans) */
  insertedText: string;
  sectionName: string;
}

export type ChangeEntry =
  | CellChangeEntry
  | ParagraphChangeEntry
  | SpanChangeEntry;

// ============================================
// 변경사항 수집
// ============================================

const isChangeCell = (cell: DiffCell): boolean =>
  cell.status === "deleted" ||
  cell.status === "inserted" ||
  cell.status === "modified" ||
  cell.sumCaution;

/** 본문/구조 변경(삭제·추가·수정) 셀 — 합계 불일치만 있는 셀은 제외 */
const isContentChangeCell = (cell: DiffCell): boolean =>
  cell.status === "deleted" ||
  cell.status === "inserted" ||
  cell.status === "modified";

/**
 * 네비게이터에 포함할 테이블 셀인지 (뷰 모드별)
 * - 전체·합계+변경: 합계·본문 변경 모두
 * - 변경만: 본문/구조 변경 셀만 (sumCaution 단독 제외)
 * - 합계만: sumCaution(단수 차이 포함)만
 */
const cellMatchesNavigatorMode = (
  cell: DiffCell,
  mode: DsdDsdViewMode,
): boolean => {
  const allKinds =
    mode === "default" || (isSumDiffsActive(mode) && isChangesActive(mode));
  if (allKinds) return isChangeCell(cell);
  if (isChangesActive(mode) && !isSumDiffsActive(mode)) {
    return isContentChangeCell(cell);
  }
  if (isSumDiffsActive(mode) && !isChangesActive(mode)) {
    return cell.sumCaution;
  }
  return false;
};

/** 문단/span 네비 엔트리 — 합계 전용 모드에서는 제외 */
const paragraphNavigatorAllowed = (mode: DsdDsdViewMode): boolean =>
  mode === "default" ||
  (isSumDiffsActive(mode) && isChangesActive(mode)) ||
  (isChangesActive(mode) && !isSumDiffsActive(mode));

/**
 * 문단이 실제로 변경되었는지 판별.
 * 백엔드가 group 단위로 status를 "modified"로 표기하는 경우가 있으므로,
 * spans에 실제 변경(insert/delete)이 있는지 확인.
 */
export const isChangeParagraph = (p: DiffParagraph): boolean => {
  if (p.status === "equal") return false;
  if (p.status === "deleted" || p.status === "inserted") return true;
  return p.spans.length > 0 && p.spans.some((s) => s.kind !== "equal");
};

/**
 * 문단의 spans에서 연속된 변경 그룹의 [start, end) 범위를 반환.
 * e.g. [equal, delete, insert, equal, delete, equal] → [[1,3], [4,5]]
 */
export const groupChangedSpans = (spans: DiffSpan[]): [number, number][] => {
  const groups: [number, number][] = [];
  let start = -1;
  for (let i = 0; i <= spans.length; i++) {
    const isChanged = i < spans.length && spans[i].kind !== "equal";
    if (isChanged && start === -1) {
      start = i;
    } else if (!isChanged && start !== -1) {
      groups.push([start, i]);
      start = -1;
    }
  }
  return groups;
};

export const buildChangeList = (
  items: DsdDsdCompareItem[],
  mode: DsdDsdViewMode = "default",
): ChangeEntry[] => {
  const entries: ChangeEntry[] = [];

  for (const [itemIdx, item] of items.entries()) {
    if (item.unitType === "table") {
      collectTableChanges(item, entries, mode);
    } else {
      collectParagraphChanges(item, itemIdx, entries, mode);
    }
  }

  return entries;
};

function collectTableChanges(
  item: DsdDsdCompareItem,
  entries: ChangeEntry[],
  mode: DsdDsdViewMode,
) {
  if (item.kind === "matched") {
    const srcTable = item.srcElement as DiffDsdTable | null;
    const trgTable = item.trgElement as DiffDsdTable | null;

    const trgByAddr = new Map<string, DiffCell>();
    if (trgTable) {
      for (const row of trgTable.rows) {
        for (const cell of row.cells) {
          trgByAddr.set(cell.address, cell);
        }
      }
    }

    const seenAddresses = new Set<string>();

    if (srcTable) {
      for (const row of srcTable.rows) {
        for (const cell of row.cells) {
          if (cell.isGhost) continue;
          if (!cellMatchesNavigatorMode(cell, mode)) continue;

          seenAddresses.add(cell.address);
          const trgCell = trgByAddr.get(cell.address) ?? null;

          entries.push({
            type: "cell",
            cellIds: [cell.cellId, ...(trgCell ? [trgCell.cellId] : [])],
            srcCell: cell,
            trgCell: trgCell,
            sectionName: item.sectionName,
          });
        }
      }
    }

    if (trgTable) {
      for (const row of trgTable.rows) {
        for (const cell of row.cells) {
          if (cell.isGhost) continue;
          if (seenAddresses.has(cell.address)) continue;
          if (!cellMatchesNavigatorMode(cell, mode)) continue;

          entries.push({
            type: "cell",
            cellIds: [cell.cellId],
            srcCell: null,
            trgCell: cell,
            sectionName: item.sectionName,
          });
        }
      }
    }
  } else {
    const table = item.element as DiffDsdTable | null;
    if (!table) return;

    for (const row of table.rows) {
      for (const cell of row.cells) {
        if (cell.isGhost) continue;
        if (!cellMatchesNavigatorMode(cell, mode)) continue;

        entries.push({
          type: "cell",
          cellIds: [cell.cellId],
          srcCell: item.kind === "deleted" ? cell : null,
          trgCell: item.kind === "inserted" ? cell : null,
          sectionName: item.sectionName,
        });
      }
    }
  }
}

function collectParagraphChanges(
  item: DsdDsdCompareItem,
  itemIdx: number,
  entries: ChangeEntry[],
  mode: DsdDsdViewMode,
) {
  if (!paragraphNavigatorAllowed(mode)) return;

  if (item.kind === "matched") {
    const srcGroup = item.srcElement as DiffParagraphGroup | null;
    const trgGroup = item.trgElement as DiffParagraphGroup | null;
    const srcParas = srcGroup?.paragraphs ?? [];
    const trgParas = trgGroup?.paragraphs ?? [];
    const len = Math.max(srcParas.length, trgParas.length);

    for (let i = 0; i < len; i++) {
      const srcP = srcParas[i] ?? null;
      const trgP = trgParas[i] ?? null;

      const srcChanged = srcP && isChangeParagraph(srcP);
      const trgChanged = trgP && isChangeParagraph(trgP);
      if (!srcChanged && !trgChanged) continue;

      // modified 쪽 → span-level entries
      // src spans에는 delete만, trg spans에는 insert만 있으므로 양쪽에서 조합
      const srcModified =
        srcChanged && srcP!.status === "modified" ? srcP! : null;
      const trgModified =
        trgChanged && trgP!.status === "modified" ? trgP! : null;
      const refP = srcModified ?? trgModified;

      if (refP) {
        const groups = groupChangedSpans(refP.spans);
        for (const [gi, [start, end]] of groups.entries()) {
          const srcSpans = srcModified?.spans.slice(start, end) ?? [];
          const trgSpans = trgModified?.spans.slice(start, end) ?? [];
          entries.push({
            type: "span",
            spanChangeId: `span_${itemIdx}_${i}_${gi}`,
            spanRange: [start, end],
            deletedText: srcSpans
              .filter((s) => s.kind === "delete")
              .map((s) => s.text)
              .join(""),
            insertedText: trgSpans
              .filter((s) => s.kind === "insert")
              .map((s) => s.text)
              .join(""),
            sectionName: item.sectionName,
          });
        }
      }

      // 양쪽 모두 modified면 span entries로 충분
      if (srcP?.status === "modified" && trgP?.status === "modified") continue;

      // deleted/inserted 쪽 → paragraph-level entry (modified와 공존 가능)
      const hasNonModifiedChange =
        (srcChanged && srcP!.status !== "modified") ||
        (trgChanged && trgP!.status !== "modified");

      if (hasNonModifiedChange) {
        entries.push({
          type: "paragraph",
          paragraphId: `para_${itemIdx}_${i}`,
          srcParagraph: srcChanged ? srcP : null,
          trgParagraph: trgChanged ? trgP : null,
          sectionName: item.sectionName,
        });
      }
    }
  } else {
    const group = item.element as DiffParagraphGroup | null;
    if (!group) return;

    for (const [i, p] of group.paragraphs.entries()) {
      if (!isChangeParagraph(p)) continue;

      entries.push({
        type: "paragraph",
        paragraphId: `para_${itemIdx}_${i}`,
        srcParagraph: item.kind === "deleted" ? p : null,
        trgParagraph: item.kind === "inserted" ? p : null,
        sectionName: item.sectionName,
      });
    }
  }
}

// ============================================
// 역방향 맵
// ============================================

export const buildCellIdToChangeIndex = (
  entries: ChangeEntry[],
): Map<string, number> => {
  const map = new Map<string, number>();
  for (const [idx, entry] of entries.entries()) {
    if (entry.type === "cell") {
      for (const cellId of entry.cellIds) {
        map.set(cellId, idx);
      }
    }
  }
  return map;
};

export const buildParagraphIdToChangeIndex = (
  entries: ChangeEntry[],
): Map<string, number> => {
  const map = new Map<string, number>();
  for (const [idx, entry] of entries.entries()) {
    if (entry.type === "paragraph") {
      map.set(entry.paragraphId, idx);
    }
  }
  return map;
};

export const buildSpanIdToChangeIndex = (
  entries: ChangeEntry[],
): Map<string, number> => {
  const map = new Map<string, number>();
  for (const [idx, entry] of entries.entries()) {
    if (entry.type === "span") {
      map.set(entry.spanChangeId, idx);
    }
  }
  return map;
};

// ============================================
// 상태 칩 설정
// ============================================

type ChipVariant = "sumMismatch" | "modified" | "deleted" | "inserted";

const CHIP_CONFIG: Record<
  ChipVariant,
  { label: string; icon: typeof TriangleAlert; className: string }
> = {
  sumMismatch: {
    label: "합계 불일치",
    icon: TriangleAlert,
    className: "bg-primary-base text-static-white",
  },
  modified: {
    label: "수정",
    icon: CircleDashed,
    className: "bg-indigo-500 text-static-white",
  },
  deleted: {
    label: "삭제",
    icon: CircleX,
    className: "bg-red-500 text-static-white",
  },
  inserted: {
    label: "추가",
    icon: CirclePlus,
    className: "bg-lime-500 text-static-white",
  },
};

/** 클릭한 셀(activeCellId) 기준으로 src/trg 중 하나를 결정 */
const resolveActiveCell = (
  entry: CellChangeEntry,
  activeCellId?: string | null,
): DiffCell | null => {
  if (activeCellId) {
    if (entry.trgCell?.cellId === activeCellId) return entry.trgCell;
    if (entry.srcCell?.cellId === activeCellId) return entry.srcCell;
  }
  return entry.srcCell ?? entry.trgCell;
};

const getChipVariants = (
  entry: ChangeEntry,
  activeCellId?: string | null,
  mode: DsdDsdViewMode = "default",
): ChipVariant[] => {
  if (entry.type === "span") return ["modified"];

  if (entry.type === "paragraph") {
    // misaligned 인덱스에서 "equal" 쪽이 아닌 실제 변경된 쪽의 status 사용
    const srcStatus =
      entry.srcParagraph && isChangeParagraph(entry.srcParagraph)
        ? entry.srcParagraph.status
        : undefined;
    const trgStatus =
      entry.trgParagraph && isChangeParagraph(entry.trgParagraph)
        ? entry.trgParagraph.status
        : undefined;
    const status = srcStatus ?? trgStatus;
    if (status === "deleted") return ["deleted"];
    if (status === "inserted") return ["inserted"];
    return ["modified"];
  }

  const cell = resolveActiveCell(entry, activeCellId);
  if (!cell) return [];

  const variants: ChipVariant[] = [];
  if (cell.sumCaution) variants.push("sumMismatch");
  if (cell.status === "modified") variants.push("modified");
  else if (cell.status === "deleted") variants.push("deleted");
  else if (cell.status === "inserted") variants.push("inserted");

  // 모드별 필터링
  if (mode === "changes") {
    return variants.filter((v) => v !== "sumMismatch");
  }
  if (mode === "sum_diffs" && cell.sumCaution) {
    return variants.filter((v) => v === "sumMismatch");
  }
  return variants;
};

const StatusChip = ({ variant }: { variant: ChipVariant }) => {
  const { label, icon: Icon, className } = CHIP_CONFIG[variant];
  return (
    <TextChip
      size="large"
      leftIcon={<Icon className="size-full" />}
      className={className}
    >
      {label}
    </TextChip>
  );
};

// ============================================
// ChangeNavigatorPanel
// ============================================

export interface ChangeNavigatorPanelProps {
  entries: ChangeEntry[];
  currentIndex: number;
  activeCellId?: string | null;
  mode?: DsdDsdViewMode;
  onNavigate: (index: number) => void;
  onClose: () => void;
}

/** 입력 중일 때는 화살표 키로 페이지 네비게이션을 가로채지 않음 */
const isArrowKeyNavigationTarget = (target: EventTarget | null): boolean => {
  if (!(target instanceof HTMLElement)) return false;
  if (target.closest("input, textarea, select, [contenteditable='true']")) {
    return true;
  }
  return false;
};

export const ChangeNavigatorPanel = ({
  entries,
  currentIndex,
  activeCellId,
  mode = "default",
  onNavigate,
}: ChangeNavigatorPanelProps) => {
  useEffect(() => {
    if (entries.length === 0) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key !== "ArrowLeft" && e.key !== "ArrowRight") return;
      if (isArrowKeyNavigationTarget(e.target)) return;

      if (e.key === "ArrowLeft") {
        if (currentIndex <= 0) return;
        e.preventDefault();
        onNavigate(currentIndex - 1);
        return;
      }
      if (currentIndex >= entries.length - 1) return;
      e.preventDefault();
      onNavigate(currentIndex + 1);
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [entries.length, currentIndex, onNavigate]);

  if (entries.length === 0) return null;

  const entry = entries[currentIndex];
  if (!entry) return null;

  const chips = getChipVariants(entry, activeCellId, mode);

  const goPrev = () => {
    if (currentIndex > 0) onNavigate(currentIndex - 1);
  };
  const goNext = () => {
    if (currentIndex < entries.length - 1) onNavigate(currentIndex + 1);
  };

  return (
    <div
      className="fixed bottom-6 right-6 z-50 w-[320px] overflow-clip rounded-xl border border-line-normal bg-white shadow-[0px_20px_25px_-5px_rgba(0,0,0,0.1),0px_4px_24px_4px_rgba(0,0,0,0.08)]"
      onClick={(e) => e.stopPropagation()}
    >
      {/* 상태 칩 */}
      <div className="flex items-start gap-1 border-b border-line-normal p-4">
        {chips.map((variant) => (
          <StatusChip key={variant} variant={variant} />
        ))}
      </div>

      {/* 상세 정보 + 네비게이션 */}
      <div className="flex flex-col gap-3 px-4 py-3">
        {entry.type === "cell" && (
          <CellChangeInfo
            entry={entry}
            activeCellId={activeCellId}
            mode={mode}
          />
        )}
        {entry.type === "paragraph" && <ParagraphChangeInfo entry={entry} />}
        {entry.type === "span" && <SpanChangeInfo entry={entry} />}

        <div className="flex items-center justify-end gap-3">
          <span className="label-2-medium">
            <span className="text-label-normal">{currentIndex + 1}</span>
            <span className="text-label-alternative">/{entries.length}</span>
          </span>
          <div className="flex items-center overflow-clip border border-line-normal">
            <button
              onClick={goPrev}
              disabled={currentIndex === 0}
              className="bg-white p-3 hover:bg-neutral-800/[.045] active:bg-neutral-900/[.09] disabled:opacity-30"
            >
              <ChevronLeft className="size-4" />
            </button>
            <div className="w-px self-stretch bg-line-normal" />
            <button
              onClick={goNext}
              disabled={currentIndex === entries.length - 1}
              className="bg-white p-3 hover:bg-neutral-800/[.045] active:bg-neutral-900/[.09] disabled:opacity-30"
            >
              <ChevronRight className="size-4" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// ============================================
// 변경 정보 표시
// ============================================

/** 회계 괄호 표기 "(1,234)" → -1234 등을 포함하여 숫자 파싱 */
const parseFormattedNumber = (value: string): number | null => {
  const trimmed = value.trim();
  const isParenNegative = trimmed.startsWith("(") && trimmed.endsWith(")");
  const stripped = isParenNegative
    ? trimmed.slice(1, -1).replace(/,/g, "")
    : trimmed.replace(/,/g, "");
  const num = Number(stripped);
  if (isNaN(num)) return null;
  return isParenNegative ? -num : num;
};

const formatAccountingNumber = (n: number): string =>
  n < 0 ? `(${Math.abs(n).toLocaleString()})` : n.toLocaleString();

/** value 문자열을 숫자로 파싱 후 diff를 더해 포맷된 자동합계를 반환 */
const computeAutoSum = (value: string, diff: number): string | null => {
  const parsed = parseFormattedNumber(value);
  if (parsed == null) return null;
  return formatAccountingNumber(parsed + diff);
};

const CellChangeInfo = ({
  entry,
  activeCellId,
  mode = "default",
}: {
  entry: CellChangeEntry;
  activeCellId?: string | null;
  mode?: DsdDsdViewMode;
}) => {
  const cell = resolveActiveCell(entry, activeCellId);
  if (!cell) return null;

  // changes 모드에서는 sum 정보 숨김
  const hasSumCaution =
    mode !== "changes" &&
    cell.sumCaution &&
    cell.sumDifference != null &&
    cell.sumDifference !== 0;

  // sum_diffs + modified + sumCaution: sum 중심 표시 (변경 정보 숨김)
  if (mode === "sum_diffs" && hasSumCaution && cell.status === "modified") {
    const autoSum = computeAutoSum(cell.value, cell.sumDifference!);
    return (
      <>
        <ValueRow label="입력한 값" value={cell.value} />
        {autoSum != null && <ValueRow label="계산된 합" value={autoSum} />}
        <ValueRow
          label="단수 차이"
          value={formatDifference(cell.sumDifference!)}
        />
      </>
    );
  }

  // modified + 단수차이: 기존 값 → 계산된 합 → 단수 차이 → 수정된 값
  if (cell.status === "modified" && entry.srcCell && entry.trgCell) {
    if (hasSumCaution) {
      const autoSum = computeAutoSum(cell.value, cell.sumDifference!);
      return (
        <>
          <ValueRow label="기존 값" value={entry.srcCell.value} />
          {autoSum != null && <ValueRow label="계산된 합" value={autoSum} />}
          <ValueRow
            label="단수 차이"
            value={formatDifference(cell.sumDifference!)}
          />
          <ValueRow label="수정된 값" value={entry.trgCell.value} />
        </>
      );
    }
    return (
      <>
        <ValueRow label="기존 값" value={entry.srcCell.value} />
        <ValueRow label="수정된 값" value={entry.trgCell.value} />
      </>
    );
  }

  // inserted + 단수차이: 추가된 값 → 계산된 합 → 단수 차이
  if (cell.status === "inserted" && hasSumCaution) {
    const autoSum = computeAutoSum(cell.value, cell.sumDifference!);
    return (
      <>
        <ValueRow label="추가된 값" value={cell.value} />
        {autoSum != null && <ValueRow label="계산된 합" value={autoSum} />}
        <ValueRow
          label="단수 차이"
          value={formatDifference(cell.sumDifference!)}
        />
      </>
    );
  }

  // 단수차이 단독: 입력한 값 → 계산된 합 → 단수 차이
  if (hasSumCaution) {
    const autoSum = computeAutoSum(cell.value, cell.sumDifference!);
    return (
      <>
        <ValueRow label="입력한 값" value={cell.value} />
        {autoSum != null && <ValueRow label="계산된 합" value={autoSum} />}
        <ValueRow
          label="단수 차이"
          value={formatDifference(cell.sumDifference!)}
        />
      </>
    );
  }

  const isSrc = cell === entry.srcCell;
  const valueLabel = getValueLabel(cell, isSrc);
  return <ValueRow label={valueLabel} value={cell.value} />;
};

const getValueLabel = (cell: DiffCell, isSrc: boolean): string => {
  if (cell.status === "deleted") return "삭제된 값";
  if (cell.status === "inserted") return "추가된 값";
  if (cell.status === "modified") return isSrc ? "기존 값" : "수정된 값";
  if (cell.sumCaution) return "합계 액";
  return "값";
};

const ParagraphChangeInfo = ({ entry }: { entry: ParagraphChangeEntry }) => {
  const p = entry.srcParagraph ?? entry.trgParagraph;
  if (!p) return null;

  return (
    <div className="body-2-normal-medium line-clamp-3 rounded-lg bg-neutral-100 p-3 text-label-normal">
      {p.text}
    </div>
  );
};

const SpanChangeInfo = ({ entry }: { entry: SpanChangeEntry }) => (
  <>
    {entry.deletedText && <ValueRow label="기존" value={entry.deletedText} />}
    {entry.insertedText && <ValueRow label="변경" value={entry.insertedText} />}
  </>
);

const ValueRow = ({ label, value }: { label: string; value: string }) => (
  <div className="flex flex-col gap-1">
    <span className="label-2-medium text-label-alternative">{label}</span>
    <div className="rounded-lg bg-neutral-100 p-3">
      <span className="body-2-normal-medium block truncate text-label-normal">
        {value || "\u00A0"}
      </span>
    </div>
  </div>
);

const formatDifference = (diff: number): string =>
  (diff > 0 ? "+" : "") + diff.toLocaleString();
